import { useState } from 'react';
import { X } from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  advertisedProductKey,
  parseProductMetadataPayload,
  splitEnteredProductIds,
} from '@/utils/spCampaignCreateDraft';
import { addTypedProductsFromText } from '@/utils/spAdGroupCreate';
import type { SpAdvertisedProduct } from '@/types/spCampaignCreate';
import { Spinner } from '@/components/ui/Spinner';

async function lookupProductsByIds(
  profileId: string,
  asins: string[],
  skus: string[]
): Promise<SpAdvertisedProduct[]> {
  const requests: Promise<SpAdvertisedProduct[]>[] = [];
  const load = async (body: Record<string, unknown>) => {
    const res = await adsApi.getProductMetadata(profileId, {
      adType: 'SP',
      checkItemDetails: true,
      checkEligibility: true,
      pageIndex: 0,
      ...body,
    });
    if (!res.success) throw new Error(res.message || 'Could not load products.');
    return parseProductMetadataPayload(res.data);
  };
  if (asins.length) {
    requests.push(load({ asins, pageSize: Math.min(100, Math.max(25, asins.length)) }));
  }
  if (skus.length) {
    requests.push(load({ skus, pageSize: Math.min(100, Math.max(25, skus.length)) }));
  }
  const pages = await Promise.allSettled(requests);
  return pages.flatMap((page) => (page.status === 'fulfilled' ? page.value : []));
}

export function TypedProductList({
  profileId,
  products,
  onChange,
  disabled,
  framed = true,
}: {
  profileId: string;
  products: SpAdvertisedProduct[];
  onChange: (products: SpAdvertisedProduct[]) => void;
  disabled?: boolean;
  framed?: boolean;
}) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const addTyped = async () => {
    const ids = text.trim();
    if (!ids || disabled) return;
    setLoading(true);
    setMessage(null);
    try {
      const parsed = addTypedProductsFromText(ids, products);
      let metadata: SpAdvertisedProduct[] = [];
      if (parsed.ids.length > 0) {
        try {
          const { asins, skus } = splitEnteredProductIds(parsed.ids);
          metadata = await lookupProductsByIds(profileId, asins, skus);
        } catch {
          // Typed IDs still submit if lookup fails.
        }
      }
      const result = addTypedProductsFromText(ids, products, metadata);
      onChange(result.products);
      setMessage(result.message);
      if (result.ids.length > 0) setText('');
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <div className="space-y-3">
      <p className="text-xs text-foreground-muted">
        Type ASINs or SKUs (one per line) and add them. Search is not required.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void addTyped();
              }
            }}
            placeholder="Enter ASINs or SKUs, one per line"
            className="w-full min-h-[88px] border border-border rounded-lg px-3 py-2 text-sm"
            disabled={disabled || loading}
            aria-label="ASINs or SKUs to advertise"
          />
          <button
            type="button"
            className="text-sm text-brand disabled:opacity-40"
            disabled={disabled || loading || !text.trim()}
            onClick={() => void addTyped()}
          >
            {loading ? 'Adding…' : 'Add products'}
          </button>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Spinner className="w-4 h-4" />
              Looking up products…
            </div>
          ) : null}
          {message ? <p className="text-xs text-foreground-muted">{message}</p> : null}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{products.length} added</p>
            {products.length > 0 ? (
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled}
                onClick={() => onChange([])}
              >
                Remove all
              </button>
            ) : null}
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-foreground-subtle border border-dashed border-border rounded-lg p-4">
              Add at least one product to advertise.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {products.map((product) => (
                <li key={advertisedProductKey(product)} className="flex items-center gap-3 p-2">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-surface-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{product.title || product.asin || product.sku}</p>
                    <p className="text-xs text-foreground-muted">
                      {product.asin || product.sku}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-foreground-muted hover:text-foreground"
                    disabled={disabled}
                    onClick={() =>
                      onChange(products.filter((item) => advertisedProductKey(item) !== advertisedProductKey(product)))
                    }
                    aria-label="Remove product"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
  if (!framed) return body;
  return (
    <fieldset className="border border-border rounded-xl p-4 space-y-3">
      <legend className="text-sm font-medium text-foreground-muted px-1">Products</legend>
      {body}
    </fieldset>
  );
}
